/**
 * Permission Constants and Group Mappings
 *
 * Defines all permissions available in the system and maps them to user groups.
 * Based on specs in docs/PERMISSIONS_SPECIFICATION.md
 */

// ============================================================================
// PERMISSION CONSTANTS
// ============================================================================

/**
 * All available permissions in the system
 * Format: 'category:action' or 'category:action:scope'
 */
export const PERMISSIONS = {
  // Search & Match
  SEARCH_PERSONS: 'search:persons',
  FACE_MATCH: 'face:match',

  // Person Management
  VIEW_PERSON: 'person:view',
  EDIT_PERSON: 'person:edit',
  DELETE_PERSON: 'person:delete',
  IMPORT_PERSONS: 'person:import',
  EXPORT_PERSONS: 'person:export',

  // Event Management - Own Events
  CREATE_EVENT: 'event:create',
  VIEW_OWN_EVENTS: 'event:view:own',
  EDIT_OWN_EVENTS: 'event:edit:own',
  DELETE_OWN_EVENTS: 'event:delete:own',

  // Event Management - All Events
  VIEW_ALL_EVENTS: 'event:view:all',
  EDIT_ALL_EVENTS: 'event:edit:all',
  DELETE_ALL_EVENTS: 'event:delete:all',

  // Event Management - Special
  CREATE_ASSIGNMENT: 'event:assignment:create',
  COMPLETE_ASSIGNMENT: 'event:assignment:complete',

  // User Management
  VIEW_USERS: 'user:view',
  EDIT_USERS: 'user:edit',
  MANAGE_USER_GROUPS: 'user:groups',
  ENABLE_DISABLE_USERS: 'user:enable',

  // System Administration
  VIEW_ADMIN_TOOLS: 'admin:tools',
  SYSTEM_CONFIG: 'admin:config',
  TENANT_ADMIN: 'admin:tenant',
  VIEW_SYSTEM_LOGS: 'admin:logs',
  MANAGE_SENDER_REGISTRY: 'admin:sender_registry',

  // Content Moderation
  MODERATE_CONTENT: 'content:moderate',
  FLAG_CONTENT: 'content:flag',
};

// ============================================================================
// GROUP PERMISSION MAPPINGS
// ============================================================================

/**
 * Maps each group to its permissions
 * Permissions are hierarchical - higher groups inherit lower group permissions
 */

// Define base permissions for each group
const GUEST_PERMS = [];

const STANDARD_PERMS = [
  PERMISSIONS.SEARCH_PERSONS,
  PERMISSIONS.FACE_MATCH,
  PERMISSIONS.VIEW_PERSON,
  PERMISSIONS.CREATE_EVENT,
  PERMISSIONS.VIEW_OWN_EVENTS,
  PERMISSIONS.EDIT_OWN_EVENTS,
  PERMISSIONS.DELETE_OWN_EVENTS,
];

const OPERATOR_PERMS = [
  ...STANDARD_PERMS,
  PERMISSIONS.VIEW_ALL_EVENTS,
  PERMISSIONS.CREATE_ASSIGNMENT,
  PERMISSIONS.COMPLETE_ASSIGNMENT,
];

const MODERATOR_PERMS = [
  ...OPERATOR_PERMS,
  PERMISSIONS.EDIT_PERSON,
  PERMISSIONS.MODERATE_CONTENT,
  PERMISSIONS.FLAG_CONTENT,
];

const ADMIN_PERMS = [
  ...MODERATOR_PERMS,
  PERMISSIONS.DELETE_PERSON,
  PERMISSIONS.IMPORT_PERSONS,
  PERMISSIONS.EXPORT_PERSONS,
  PERMISSIONS.EDIT_ALL_EVENTS,
  PERMISSIONS.DELETE_ALL_EVENTS,
  PERMISSIONS.VIEW_USERS,
  PERMISSIONS.EDIT_USERS,
  PERMISSIONS.MANAGE_USER_GROUPS,
  PERMISSIONS.ENABLE_DISABLE_USERS,
  PERMISSIONS.VIEW_ADMIN_TOOLS,
  PERMISSIONS.SYSTEM_CONFIG,
  PERMISSIONS.TENANT_ADMIN,
  PERMISSIONS.VIEW_SYSTEM_LOGS,
  PERMISSIONS.MANAGE_SENDER_REGISTRY,
];

export const GROUP_PERMISSIONS = {
  GUEST: GUEST_PERMS,
  STANDARD: STANDARD_PERMS,
  OPERATOR: OPERATOR_PERMS,
  MODERATOR: MODERATOR_PERMS,
  ADMIN: ADMIN_PERMS,
};

// Note: OPERATOR permissions are deliberately defined above, but OPERATOR
// users are currently blocked from app access (see cognitoService.js userIsBlocked())
// This needs to be clarified in the future.

// ============================================================================
// PERMISSION CATEGORIES
// ============================================================================

/**
 * Groups permissions by category for UI display
 */
export const PERMISSION_CATEGORIES = {
  'Search & Match': [
    PERMISSIONS.SEARCH_PERSONS,
    PERMISSIONS.FACE_MATCH,
  ],
  'Person Management': [
    PERMISSIONS.VIEW_PERSON,
    PERMISSIONS.EDIT_PERSON,
    PERMISSIONS.DELETE_PERSON,
    PERMISSIONS.IMPORT_PERSONS,
    PERMISSIONS.EXPORT_PERSONS,
  ],
  'Event Management': [
    PERMISSIONS.CREATE_EVENT,
    PERMISSIONS.VIEW_OWN_EVENTS,
    PERMISSIONS.EDIT_OWN_EVENTS,
    PERMISSIONS.DELETE_OWN_EVENTS,
    PERMISSIONS.VIEW_ALL_EVENTS,
    PERMISSIONS.EDIT_ALL_EVENTS,
    PERMISSIONS.DELETE_ALL_EVENTS,
    PERMISSIONS.CREATE_ASSIGNMENT,
    PERMISSIONS.COMPLETE_ASSIGNMENT,
  ],
  'User Management': [
    PERMISSIONS.VIEW_USERS,
    PERMISSIONS.EDIT_USERS,
    PERMISSIONS.MANAGE_USER_GROUPS,
    PERMISSIONS.ENABLE_DISABLE_USERS,
  ],
  'System Administration': [
    PERMISSIONS.VIEW_ADMIN_TOOLS,
    PERMISSIONS.SYSTEM_CONFIG,
    PERMISSIONS.TENANT_ADMIN,
    PERMISSIONS.VIEW_SYSTEM_LOGS,
    PERMISSIONS.MANAGE_SENDER_REGISTRY,
  ],
  'Content Moderation': [
    PERMISSIONS.MODERATE_CONTENT,
    PERMISSIONS.FLAG_CONTENT,
  ],
};

/**
 * Human-readable permission labels
 */
export const PERMISSION_LABELS = {
  [PERMISSIONS.SEARCH_PERSONS]: 'Search Persons',
  [PERMISSIONS.FACE_MATCH]: 'Face Matching',
  [PERMISSIONS.VIEW_PERSON]: 'View Person Details',
  [PERMISSIONS.EDIT_PERSON]: 'Edit Person Data',
  [PERMISSIONS.DELETE_PERSON]: 'Delete Persons',
  [PERMISSIONS.IMPORT_PERSONS]: 'Bulk Import Persons',
  [PERMISSIONS.EXPORT_PERSONS]: 'Export Person Data',
  [PERMISSIONS.CREATE_EVENT]: 'Create Events',
  [PERMISSIONS.VIEW_OWN_EVENTS]: 'View Own Events',
  [PERMISSIONS.EDIT_OWN_EVENTS]: 'Edit Own Events',
  [PERMISSIONS.DELETE_OWN_EVENTS]: 'Delete Own Events',
  [PERMISSIONS.VIEW_ALL_EVENTS]: 'View All Events',
  [PERMISSIONS.EDIT_ALL_EVENTS]: 'Edit All Events',
  [PERMISSIONS.DELETE_ALL_EVENTS]: 'Delete All Events',
  [PERMISSIONS.CREATE_ASSIGNMENT]: 'Create Assignments',
  [PERMISSIONS.COMPLETE_ASSIGNMENT]: 'Complete Assignments',
  [PERMISSIONS.VIEW_USERS]: 'View Users',
  [PERMISSIONS.EDIT_USERS]: 'Edit Users',
  [PERMISSIONS.MANAGE_USER_GROUPS]: 'Manage User Groups',
  [PERMISSIONS.ENABLE_DISABLE_USERS]: 'Enable/Disable Users',
  [PERMISSIONS.VIEW_ADMIN_TOOLS]: 'Access Admin Tools',
  [PERMISSIONS.SYSTEM_CONFIG]: 'System Configuration',
  [PERMISSIONS.TENANT_ADMIN]: 'Tenant Administration',
  [PERMISSIONS.VIEW_SYSTEM_LOGS]: 'View System Logs',
  [PERMISSIONS.MANAGE_SENDER_REGISTRY]: 'Manage Email Sender Registry',
  [PERMISSIONS.MODERATE_CONTENT]: 'Moderate Content',
  [PERMISSIONS.FLAG_CONTENT]: 'Flag Content',
};
